import { FormEvent, useMemo, useState } from "react";

import { AddWebhookActionProps } from "./types";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useCreateWebhook } from "@/queries";
import { useOrganizationSubscriptionData } from "@/providers";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { ToastType } from "@/enums/toast";

export const AddWebhookAction = ({ onWebhookCreated }: AddWebhookActionProps) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const auth = useAuth();
  const userContext = useUser();
  const { userToken } = auth!;
  const createWorkspaceWebhook = useCreateWebhook(userToken!);
  const { subscription, isLoadingSubscriptionStatus, isPLAdmin } =
    useOrganizationSubscriptionData();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const newWebhook = await createWorkspaceWebhook.mutateAsync(
        {
          url,
          workspace_id: userContext?.activeWorkspaceId ?? 0,
        },
        {
          onError: () => {
            displayToast("Failed to create webhook", ToastType.error);
          },
        },
      );
      if (newWebhook) {
        userContext.workspaceWebhooks.push(newWebhook);
        onWebhookCreated(newWebhook, newWebhook.webhook.secret_key);
        setUrl("");
        setOpen(false);
      }
    } catch {
      displayErrorToast("Failed to create webhook");
    }
  };

  const isDisabled = useMemo(
    () => createWorkspaceWebhook.isLoading || url.trim() === "",
    [createWorkspaceWebhook.isLoading, url],
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <BasicTooltip
        shouldShow={
          (subscription?.plan_type !== "team" &&
            subscription?.plan_type !== "enterprise" &&
            !isPLAdmin) ||
          isLoadingSubscriptionStatus
        }
        content={
          (subscription?.plan_type !== "team" &&
            subscription?.plan_type !== "enterprise" &&
            !isPLAdmin) ||
          isLoadingSubscriptionStatus
            ? "Upgrade to create webhooks"
            : ""
        }
      >
        <span className="flex cursor-pointer items-center">
          <DialogTrigger asChild>
            <Button
              disabled={
                (subscription?.plan_type !== "team" &&
                  subscription?.plan_type !== "enterprise" &&
                  !isPLAdmin) ||
                isLoadingSubscriptionStatus
              }
              variant="outline"
              className="flex items-center gap-2"
            >
              Create New Webhook
            </Button>
          </DialogTrigger>
        </span>
      </BasicTooltip>
      <DialogContent className="max-w-[500px] px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
        <DialogHeader>
          <DialogTitle>Add New Webhook</DialogTitle>
          <DialogDescription>
            Enter the URL where you want to receive webhook notifications.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              label="Webhook URL"
              id="url"
              name="url"
              onChange={e => setUrl(e.target.value)}
              required
              type="url"
              placeholder="https://example.com/promptlayer-webhook"
              value={url}
            />
          </div>
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              disabled={isDisabled}
              isLoading={createWorkspaceWebhook.isLoading}
              type="submit"
            >
              Add Webhook
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
